import React, { useState, useEffect } from 'react'
import '../../../assets/styles/_beveledmenu.scss'
import Menu from './Menu'
import Option from './Option'
import OverlayMenuModal from '../../molecules/OverlayModal/OverlayModal'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { useFeatureFlag } from '../../molecules/URLFeatureFlag/URLFeatureFlag'

interface StudentMenuProps {
  className?: string
}

enum MenuType {
  HELP = 'help',
  ACCESSIBILITY = 'accessibility'
}

export enum FocusableElement {
  LinkHelp = 'link-help',
  LinkAccessibility = 'link-accessibility',
  CloseButton = 'modal-close-button'
}

const StudentMenu: React.FC<StudentMenuProps> = ({ className }) => {
  const [activeMenu, setActiveMenu] = useState<MenuType | null>(null)
  const keyboardControls = useKeyboardControls()
  const urlFeatureFlagEnabled = useFeatureFlag()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (activeMenu && event.key === 'Tab') {
        event.preventDefault()
        keyboardControls.setFocusedElement(FocusableElement.CloseButton)
      }
    }

    if (activeMenu) {
      document.addEventListener('keydown', handleKeyDown)
      keyboardControls.setFocusedElement(FocusableElement.CloseButton)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeMenu, keyboardControls])

  // Here we are using the URLFeatureFlag to determine if the menu should be displayed
  if (!urlFeatureFlagEnabled) {
    return null
  }

  const openMenu = (menuType: MenuType): void => {
    setActiveMenu(menuType)
  }

  const closeMenu = (): void => {
    setActiveMenu(null)
  }

  const renderModalContent = (title: string, children: React.ReactNode): JSX.Element => (
    <div className='w-full'>
      <div className='mb-4'>
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  )

  return (
    <>
      <div data-testid='student-menu' className={`w-full pr-28 ${className ?? ''}`}>
        <Menu>
          <Option
            text='Help'
            focusableElement={FocusableElement.LinkHelp}
            onClick={() => openMenu(MenuType.HELP)}
          />
          <Option
            text='Accessibility'
            focusableElement={FocusableElement.LinkAccessibility}
            onClick={() => openMenu(MenuType.ACCESSIBILITY)}
          />
        </Menu>
      </div>

      {activeMenu === MenuType.ACCESSIBILITY && (
        <OverlayMenuModal
          onClick={closeMenu}
          overlayWidth='70vw'
          overlayHeight='80vh'
          showOverlay
        >
          {renderModalContent('Accessibility Options',
            <p /* Content */ />
          )}
        </OverlayMenuModal>
      )}

      {activeMenu === MenuType.HELP && (
        <OverlayMenuModal
          onClick={closeMenu}
          overlayWidth='70vw'
          overlayHeight='80vh'
          showOverlay
        >
          {renderModalContent('Help Options',
            <p /* Content */ />
          )}
        </OverlayMenuModal>
      )}
    </>
  )
}

export default StudentMenu
